// =============================================================
// Auth + RBAC context.
//
// Owns the full login → 6-digit MFA → session flow against the real /api/v1
// (no localStorage prototype store). After a session is established it loads
// `/auth/me` → effective GroupPermissions, and exposes `can(module, op)` /
// `seesTab(module)` bound to the core RBAC predicate so the UI can hide/disable
// per permission (the same matrix the server enforces).
// =============================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ApiError,
  can as rbacCan,
  cardTabVisible as rbacSeesTab,
  type ApiClient,
  type GroupPermissions,
  type MeResponse,
  type Operation,
  type PermModule,
} from "@perpetuum/core";
import { buildApiClient, readToken, writeToken } from "../lib/api.js";

/** Auth lifecycle phase. */
export type AuthPhase =
  | "loading" // verifying a persisted token on boot
  | "anonymous" // not signed in
  | "mfa" // password OK, awaiting the 6-digit code
  | "authenticated";

interface AuthState {
  phase: AuthPhase;
  api: ApiClient;
  /** Display info for the signed-in (or pending-MFA) user. */
  user: {
    id: string;
    name: string;
    username?: string;
    email?: string;
    companyId?: string;
    companyName?: string;
    groupName?: string;
  } | null;
  permissions: GroupPermissions | null;
  error: string | null;
  /** Step 1: username + password. Returns whether MFA is now required. */
  login: (username: string, password: string) => Promise<{ mfaRequired: boolean }>;
  /** Step 2: verify the 6-digit code (only valid in the `mfa` phase). */
  verifyMfa: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  /** RBAC: may the current principal perform `op` on `module`? */
  can: (module: PermModule, op: Operation) => boolean;
  /** RBAC: is the module card-tab visible to the current principal? */
  seesTab: (module: PermModule) => boolean;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children, api: injected }: { children: ReactNode; api?: ApiClient }) {
  // One ApiClient for the app lifetime (tests may inject one bound to the in-process Hono app).
  const apiRef = useRef<ApiClient>(injected ?? buildApiClient());
  const api = apiRef.current;

  const [phase, setPhase] = useState<AuthPhase>(() => (readToken() ? "loading" : "anonymous"));
  const [user, setUser] = useState<AuthState["user"]>(null);
  const [permissions, setPermissions] = useState<GroupPermissions | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The pre-MFA token returned by /auth/login (passed explicitly to mfa/verify).
  const [preMfaToken, setPreMfaToken] = useState<string | null>(null);

  const loadMe = useCallback(async (): Promise<void> => {
    const me: MeResponse = await api.me();
    setPermissions(me.permissions);
    // MERGE the enriched identity (do not preserve a stale pre-MFA user that
    // lacks these fields). Also fixes the reload bug where name showed the id.
    setUser({
      id: me.id,
      name: me.name ?? me.id,
      username: me.username,
      email: me.email,
      companyId: me.companyId,
      companyName: me.companyName,
      groupName: me.groupName,
    });
    setPhase("authenticated");
  }, [api]);

  // On boot, if a token is persisted, validate it via /auth/me.
  useEffect(() => {
    if (phase !== "loading") return;
    let cancelled = false;
    void (async () => {
      try {
        await loadMe();
      } catch {
        if (!cancelled) {
          writeToken(null);
          setPhase("anonymous");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, loadMe]);

  const login = useCallback<AuthState["login"]>(
    async (username, password) => {
      setError(null);
      try {
        const res = await api.login(username, password);
        setUser({ id: res.user.id, name: res.user.name, companyId: res.user.companyId });
        if (res.mfaRequired) {
          // Hold the pre-MFA token; do NOT persist it as the session yet.
          setPreMfaToken(res.token);
          setPhase("mfa");
          return { mfaRequired: true };
        }
        // No MFA: this token IS the session.
        writeToken(res.token);
        await loadMe();
        return { mfaRequired: false };
      } catch (e) {
        const msg = e instanceof ApiError ? (e.code ?? `error_${e.status}`) : "network_error";
        setError(msg);
        throw e;
      }
    },
    [api, loadMe],
  );

  const verifyMfa = useCallback<AuthState["verifyMfa"]>(
    async (code) => {
      setError(null);
      if (!preMfaToken) throw new Error("no pre-MFA token");
      try {
        const res = await api.verifyMfa(code, preMfaToken);
        writeToken(res.token);
        setPreMfaToken(null);
        await loadMe();
      } catch (e) {
        const msg = e instanceof ApiError ? (e.code ?? `error_${e.status}`) : "network_error";
        setError(msg);
        throw e;
      }
    },
    [api, preMfaToken, loadMe],
  );

  const logout = useCallback<AuthState["logout"]>(async () => {
    try {
      await api.logout();
    } catch {
      /* best-effort — clear locally regardless */
    }
    writeToken(null);
    setPreMfaToken(null);
    setUser(null);
    setPermissions(null);
    setError(null);
    setPhase("anonymous");
  }, [api]);

  const can = useCallback<AuthState["can"]>(
    (module, op) => (permissions ? rbacCan(permissions, module, op) : false),
    [permissions],
  );
  const seesTab = useCallback<AuthState["seesTab"]>(
    (module) => (permissions ? rbacSeesTab(permissions, module) : false),
    [permissions],
  );

  const value = useMemo<AuthState>(
    () => ({ phase, api, user, permissions, error, login, verifyMfa, logout, can, seesTab }),
    [phase, api, user, permissions, error, login, verifyMfa, logout, can, seesTab],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
